import { CarePlan } from "@medplum/fhirtypes";
import { MappedConsolidatedResources } from "../../../shared-logic/consolidated-context/reducer";
import { SidePanelContent } from "../../side-panel/side-panel-content";
import { getResourcesFromBundle } from "../shared";
import { DetailsDisplay } from "../shared/details";
import { CarePlanRowData } from "./table-data";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
import { ReferencesDisplay } from "../shared/references";

export function CarePlanContent({
  mappedConsolidated,
  isImpersonating,
  ...rowContents
}: {
  isImpersonating?: boolean;
  mappedConsolidated: MappedConsolidatedResources | undefined;
} & CarePlanRowData) {
  const carePlans = getResourcesFromBundle<CarePlan>(mappedConsolidated, "CarePlan");
  const selectedCarePlan = carePlans.find(carePlan => carePlan.id === rowContents.id);
  if (!selectedCarePlan) return null;

  const sourceFileName =
    (selectedCarePlan?.meta?.source ||
      selectedCarePlan?.extension?.find(
        ext => ext.url?.endsWith("doc-id-extension.json") && ext.valueString
      )?.valueString) ??
    "";

  return (
    <SidePanelContent
      title="CarePlan"
      sourceDocument={{
        id: selectedCarePlan?.id ?? "",
        fileName: sourceFileName,
      }}
      fhirJson={isImpersonating ? JSON.stringify(selectedCarePlan, null, 2) : undefined}
    >
      <CarePlanDisplay carePlan={selectedCarePlan} tableRow={rowContents} />
    </SidePanelContent>
  );
}

function CarePlanDisplay({
  carePlan,
  tableRow,
}: {
  carePlan: CarePlan | undefined;
  tableRow: CarePlanRowData;
}) {
  if (!carePlan) {
    return null;
  }

  return (
    <>
      <DetailsDisplay
        details={{
          carePlan: tableRow.description,
          date: buildDateString(tableRow.rawDate, tableRow.rawEndDate),
        }}
      />

      <ReferencesDisplay
        practitioners={{
          refs: tableRow.practitioner ? [tableRow.practitioner] : undefined,
        }}
        organizations={{
          refs: tableRow.organization ? [tableRow.organization] : undefined,
        }}
        locations={{
          refs: tableRow.location ? [tableRow.location] : undefined,
        }}
        conditions={{
          refs: tableRow.conditions ? tableRow.conditions : undefined,
        }}
      />
    </>
  );
}

function buildDateString(startDate: dayjs.Dayjs, endDate?: dayjs.Dayjs): string {
  let dateString = startDate.format(`${ISO_DATE} HH:mm`);
  if (endDate) {
    if (!startDate.isSame(endDate, "hour")) {
      dateString += ` - ${endDate.format(`${ISO_DATE} HH:mm`)}`;
    }
  }
  return dateString;
}
